import { useAnalytics } from '@contexts/AnalyticsContext'
import { useTheme } from '@contexts/ThemeContext'
import {
  FetchUserAnalyticsGraphParams,
  UserAnalyticsGraphBookingConversion,
} from '@models/user/UserAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { useAppSelector } from '@store/hooks'
import { selectSelectedUser } from '@store/UserReducer'
import { Card, Empty, Spin } from 'antd'
import { useEffect, useState } from 'react'
import {
  Funnel,
  FunnelChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import CustomTooltip from '../common/CustomTooltip'

const BookingFunnel = () => {
  const user = useAppSelector(selectSelectedUser())
  const { selectedDates } = useAnalytics()
  const { mode } = useTheme()

  const [chartData, setChartData] = useState<
    UserAnalyticsGraphBookingConversion[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchData = async () => {
    if (!user) return

    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setChartData([])
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams: FetchUserAnalyticsGraphParams = {
      entity: 'user',
      entityId: user.id,
      kind: 'booking-conversion',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
    }

    try {
      const response =
        await fetchAnalyticsGraph<UserAnalyticsGraphBookingConversion[]>(
          fetchParams
        )
      const data = response.data
      setChartData(data || [])

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [user, selectedDates])

  const allValuesAreZero = chartData.every(({ value }) => value === 0)

  return (
    <Card
      style={{ alignContent: 'center', textAlign: 'center', minHeight: 450 }}
    >
      {isLoading && <Spin />}
      {(allValuesAreZero || chartData.length === 0 || error) && !isLoading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
      )}
      {!allValuesAreZero && chartData.length > 0 && !isLoading && !error && (
        <ResponsiveContainer width={'100%'} height={380}>
          <FunnelChart>
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  type="funnel"
                  {...props}
                  customLabelFormatter={(payload) => {
                    return payload.label
                  }}
                />
              )}
            />
            <Funnel dataKey="value" data={chartData} isAnimationActive>
              <LabelList
                position="right"
                fill={mode === 'dark' ? 'white' : 'dark'}
                stroke="none"
                dataKey="name"
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}

export default BookingFunnel
